import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { ICompanyBrandingService } from "services/CompanyBrandingService";
import { NewCompanyBrandingModel } from "services/classes/NewCompanyBrandingModel";
import {
  GenerateUpdateModelFromBranding,
  UpdateCompanyBrandingModel,
} from "services/classes/UpdateCompanyBrandingModel";
import { RootState } from "setup";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import translations from "utils/translationUtility";

const getDefaultCompanyBrandingModel = () => {
  return new NewCompanyBrandingModel({
    isGeneratedCSSFile: true,
    isHeaderTextBold: false,
    isHeaderTextItalic: false,
    isHeaderTextUnderlined: false,
    clientPortalName: `${translations.client} Portal`,
    navbarBackgroundColour: "#106488",
    navbarButtonColour: "#106488",
    navbarTextColour: "#FFFFFF",
    navbarIconColour: "#FFFFFF",
    navbarButtonTextColour: "#FFFFFF",
    toolbarBackgroundColour: "#FFFFFF",
    toolbarButtonTextColour: "#FFFFFF",
    toolbarButtonColour: "#106488",
    toolbarTextColour: "#106488",
    footerBackgroundColour: "#106488",
    footerButtonTextColour: "#106488",
    footerButtonColour: "#FFFFFF",
    footerTextColour: "#FFFFFF",
    fontHeadingColour: "#000000",
    fontBodyColour: "#000000",
    buttonPrimaryColour: "#106488",
    buttonPrimaryTextColour: "#FFFFFF",
    buttonSecondaryColour: "#8F8F8F",
    buttonSecondaryTextColour: "#FFFFFF",
    buttonDangerColour: "#B3313C",
    buttonDangerTextColour: "#FFFFFF",
    buttonSuccessColour: "#286B46",
    buttonSuccessTextColour: "#FFFFFF",
    brandPrimaryColour: "#106488",
    brandSecondaryColour: "#106488",
    brandTertiaryColour: "#E2E0D4",
  });
};

const initialState = {
  IsBusy: false,
  IsBrandingUpdate: false,
  CompanyBrandingModel: new UpdateCompanyBrandingModel(getDefaultCompanyBrandingModel()),
};

export const companyBrandingSlice = createSlice({
  name: "companyBranding",
  initialState,
  reducers: {
    setIsBrandingUpdate: (state, action) => {
      state.IsBrandingUpdate = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCompanyBranding.fulfilled, (state, action) => {
        state.CompanyBrandingModel = action.payload;
        state.IsBusy = false;
      })
      .addCase(getCompanyBranding.pending, (state) => {
        state.IsBusy = true;
      })
      .addCase(getCompanyBranding.rejected, (state) => {
        state.IsBusy = false;
      })
      .addCase(createCompanyBranding.fulfilled, (state) => {
        toast.success("Creation Successful");
        state.IsBusy = false;
      })
      .addCase(createCompanyBranding.pending, (state) => {
        state.IsBusy = true;
      })
      .addCase(createCompanyBranding.rejected, (state) => {
        state.IsBusy = false;
      })
      .addCase(updateCompanyBranding.fulfilled, (state) => {
        toast.success("Save Successful");
        state.IsBusy = false;
      })
      .addCase(updateCompanyBranding.pending, (state) => {
        state.IsBusy = true;
      })
      .addCase(updateCompanyBranding.rejected, (state) => {
        state.IsBusy = false;
      });
  },
});

export const getCompanyBranding = createAsyncThunk<UpdateCompanyBrandingModel, object, { state: RootState }>(
  "companyBranding/getCompanyBranding",
  async (_, thunkAPI) => {
    const companyBrandingService = container.get<ICompanyBrandingService>(SERVICE_TYPES.CompanyBrandingService);
    const companyBranding = await companyBrandingService.getCompanyBranding();
    if (companyBranding == null) {
      return getDefaultCompanyBrandingModel();
    }
    thunkAPI.dispatch(setIsBrandingUpdate(true));
    return GenerateUpdateModelFromBranding(companyBranding);
  }
);

export const createCompanyBranding = createAsyncThunk(
  "companyBranding/createCompanyBranding",
  async ({ companyBranding }: { companyBranding: NewCompanyBrandingModel }) => {
    const companyBrandingService = container.get<ICompanyBrandingService>(SERVICE_TYPES.CompanyBrandingService);
    return await companyBrandingService.createNewCompanyBranding(companyBranding);
  }
);

export const updateCompanyBranding = createAsyncThunk(
  "companyBranding/updateCompanyBranding",
  async ({ companyBranding }: { companyBranding: UpdateCompanyBrandingModel }) => {
    const companyBrandingService = container.get<ICompanyBrandingService>(SERVICE_TYPES.CompanyBrandingService);
    return await companyBrandingService.updateCompanyBranding(companyBranding);
  }
);

export default companyBrandingSlice.reducer;

export const { setIsBrandingUpdate } = companyBrandingSlice.actions;

export const selectCompanyBranding = (state: RootState) => {
  return state.companyBranding.CompanyBrandingModel;
};
export const selectIsBrandingUpdate = (state: RootState) => {
  return state.companyBranding.IsBrandingUpdate;
};

export const selectIsBusy = (state: RootState) => {
  return state.companyBranding.IsBusy;
};
